import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Zap, Shield, Code } from 'lucide-react';
import SignupFormModal from '../components/SignupFormModal';

export default function IntegrationsPage() {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const [modalButtonType, setModalButtonType] = useState('demo');

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Update the messagingIntegrations array (around line 22):

    const messagingIntegrations = [
        {
            name: "WhatsApp Business API",
            description: "Official API integration for instant messaging and automated responses.",
            logo: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg",
        },
        {
            name: "Gmail",
            description: "Automated email responses with full conversation tracking.",
            logo: "https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg",
        },
        {
            name: "Outlook",
            description: "Seamless integration with Microsoft email ecosystem.",
            logo: "https://upload.wikimedia.org/wikipedia/commons/9/94/Microsoft_Office_Outlook_%282013%E2%80%932019%29.svg",
        },
        {
            name: "Twilio",
            description: "SMS automation and programmable messaging.",
            logo: "https://cdn.worldvectorlogo.com/logos/twilio.svg",
        }
    ];

    // Then update the rendering section (around line 270):

    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {messagingIntegrations.map((integration, index) => (
            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <img
                        src={integration.logo}
                        alt={integration.name}
                        className="w-full h-full object-contain"
                    />
                </div>
                <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                <p className="text-sm text-gray-600">{integration.description}</p>
            </div>
        ))}
    </div>

    const crmIntegrations = [
        {
            name: "HubSpot",
            description: "Complete CRM integration with contact management and sales pipelines.",
            logo: "https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png",
        },
        {
            name: "Salesforce",
            description: "Enterprise CRM sync for large-scale sales operations.",
            logo: "https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg",
        },
        {
            name: "Zoho",
            description: "Integrated suite for sales, marketing, and customer support.",
            logo: "https://cdn.brandfetch.io/idssig0_jY/theme/dark/logo.svg?c=1bxid64Mup7aczewSAYMX&t=1687855848599",
        },
        {
            name: "Pipedrive",
            description: "Sales pipeline management with automatic lead routing.",
            logo: "https://cdn.worldvectorlogo.com/logos/pipedrive.svg",
        }
    ];

    const advertisingIntegrations = [
        { name: "Google Ads", description: "Automatic lead capture from search and display campaigns.", icon: "G", color: "text-red-500" },
        { name: "Meta Ads", description: "Facebook and Instagram lead generation with instant follow-up.", icon: "f", color: "text-blue-600" },
        { name: "TikTok Ads", description: "Capture leads from TikTok campaigns instantly.", icon: "♪", color: "text-black" },
        { name: "LinkedIn Campaigns", description: "B2B lead generation with professional targeting.", icon: "in", color: "text-blue-700" }
    ];

    const schedulingIntegrations = [
        { name: "Calendly", description: "Seamless appointment scheduling automation.", icon: "📅", color: "text-blue-400" },
        { name: "Google Calendar", description: "Direct sync with Google workspace calendar.", icon: "G", color: "text-red-500" },
        { name: "Outlook Calendar", description: "Microsoft calendar integration for enterprises.", icon: "📆", color: "text-orange-500" }
    ];

    const dataAutomationIntegrations = [
        {
            name: "Notion",
            description: "Sync leads and notes to your Notion workspace.",
            logo: "https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png",
        },
        {
            name: "Airtable",
            description: "Database integration for custom workflows.",
            logo: "https://cdn.worldvectorlogo.com/logos/airtable-1.svg",
        },
        {
            name: "Zapier",
            description: "Connect 5,000+ apps with no-code automation.",
            logo: "https://cdn.worldvectorlogo.com/logos/zapier.svg",
        },
        {
            name: "Make (Integromat)",
            description: "Visual automation platform for complex workflows.",
            logo: "https://cdn.brandfetch.io/idVHU5hl7_/theme/dark/logo.svg?c=1bxid64Mup7aczewSAYMX&t=1690469454303",
        }
    ];
    const aiLayerIntegrations = [
        {
            name: "OpenAI GPT-5",
            description: "Advanced natural language processing and generation.",
            logo: "https://upload.wikimedia.org/wikipedia/commons/0/04/ChatGPT_logo.svg",
        },
        {
            name: "Whisper",
            description: "Voice-to-text transcription for voice messages.",
            logo: "https://cdn-icons-png.flaticon.com/512/9790/9790561.png",
        },
        {
            name: "LangChain",
            description: "Framework for building AI conversation chains.",
            logo: "https://cdn.brandfetch.io/idzf7Sjo28/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1767358843567",
        },
        {
            name: "ElevenLabs Voice",
            description: "Realistic AI voice generation for audio responses.",
            logo: "https://cdn.brandfetch.io/idl_sWY35e/w/400/h/400/theme/dark/icon.jpeg?c=1bxid64Mup7aczewSAYMX&t=1741336798391",
        }
    ];

    const whyIntegrationsWork = [
        { title: "Instant Sync", description: "All integrations update in real-time. No delays, no data loss.", Icon: Zap },
        { title: "Zero Coding", description: "Connect in clicks. No technical knowledge required.", Icon: Code },
        { title: "Enterprise Security", description: "Bank-level encryption. 100% GDPR compliant.", Icon: Shield }
    ];

    return (
        <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
        
        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

            {/* Navigation */}
            <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <Link to="/" className="flex items-center gap-2">
                            <img
                                src="/logo-qorvix.png"
                                alt="Qorvix Logo"
                                className="h-10 w-auto"
                            />
                        </Link>
                        <div className="hidden lg:flex items-center space-x-8">
                            <Link to="/" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
                            <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
                            <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
                            <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
                            <div className="relative group">
                                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                                    Resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                    <div className="py-2">
                                        <a href="/documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</a>
                                        <Link to="/integrations" className="block px-4 py-2.5 text-orange-500 bg-gray-50 transition-colors">Integration</Link>
                                        <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                                        <a href="/help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</a>
                                        <Link to="/roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</Link>
                                    </div>
                                </div>
                            </div>
                            <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="bg-transparent border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg hover:bg-orange-500 hover:text-white transition-all font-medium text-[15px]">
                                Start Free Demo
                            </button>
                        </div>

                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="lg:hidden text-gray-900"
                        >
                            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Modal - Add this after </nav> */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>

                    {/* Menu Panel */}
                    <div className="fixed top-8 left-1/2 -translate-x-1/2 w-[90%] max-w-sm bg-white rounded-3xl shadow-2xl p-8 z-50">
                        {/* Close Button */}
                        <button
                            onClick={() => setMobileMenuOpen(false)}
                            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                        >
                            <X size={24} />
                        </button>

                        {/* Menu Items */}
                        <nav className="mt-4 space-y-6">
                            <Link
                                to="/"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Home
                            </Link>
                            <Link
                                to="/about"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                About
                            </Link>
                            <Link
                                to="/how-it-works"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                How it works
                            </Link>
                            <Link
                                to="/portfolio"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                portfolio
                            </Link>

                            {/* Resources Dropdown */}
                            <div>
                                <button className="w-full text-left text-lg font-medium text-gray-900 flex items-center gap-2">
                                    resources
                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="mt-1">
                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                                <div className="mt-4 ml-4 space-y-4">
                                    <Link
                                        to="/documentation"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Documentation
                                    </Link>
                                    <Link
                                        to="/integrations"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Integrations
                                    </Link>
                                    <Link
                                        to="/faq"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        FAQs
                                    </Link>
                                    <Link
                                        to="/help-center"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Help center
                                    </Link>
                                    <Link
                                        to="/roadmap"
                                        className="block text-base text-gray-700 hover:text-orange-500 transition-colors"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Roadmap
                                    </Link>
                                </div>
                            </div>

                            <Link
                                to="/contact"
                                className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Contact us
                            </Link>

                            {/* CTA Button */}
                            <button onClick={() => {
                                setModalButtonType('demo');
                                setIsSignupModalOpen(true);
                            }} className="text-lg font-medium text-orange-500 border-b-2 border-orange-500 pb-1">
                                Start free demo
                            </button>
                        </nav>
                    </div>
                </div>
            )}
            {/* Hero Section */}
            <section className="pt-32 pb-16 px-4 bg-gradient-to-br from-orange-50 via-white to-orange-50">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-5xl md:text-6xl font-bold mb-6">
                        <span className="text-gradient">Integrations</span>
                    </h1>
                    <p className="text-lg text-gray-600">
                        Qorvix connects with everything you already use. very integration syncs instantly — zero coding, zero waiting.
                    </p>
                </div>
            </section>

            {/* Messaging */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">Messaging</h2>
                        <p className="text-gray-600">Connect your communication channels for instant customer engagement</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {messagingIntegrations.map((integration, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow text-center">
                                <div className="w-16 h-16 mx-auto mb-4">
                                    <img
                                        src={integration.logo}
                                        alt={integration.name}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                                <p className="text-sm text-gray-600">{integration.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CRM & Sales */}
            <section className="py-16 px-4 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">CRM & Sales</h2>
                        <p className="text-gray-600">Sync leads and conversations directly to your customer management system</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {crmIntegrations.map((integration, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow text-center">
                                <div className="w-16 h-16 mx-auto mb-4">
                                    <img
                                        src={integration.logo}
                                        alt={integration.name}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                                <p className="text-sm text-gray-600">{integration.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Advertising */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">Advertising</h2>
                        <p className="text-gray-600">Connect ad platforms for instant lead capture and response</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {advertisingIntegrations.map((integration, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow text-center">
                                <div className={`text-5xl font-bold mb-4 ${integration.color}`}>{integration.icon}</div>
                                <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                                <p className="text-sm text-gray-600">{integration.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Scheduling */}
            <section className="py-16 px-4 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">Scheduling</h2>
                        <p className="text-gray-600">Automate appointment booking with calendar integrations</p>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                        {schedulingIntegrations.map((integration, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow text-center">
                                <div className={`text-5xl mb-4 ${integration.color}`}>{integration.icon}</div>
                                <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                                <p className="text-sm text-gray-600">{integration.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Data & Automation */}
            <section className="py-16 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">Data & Automation</h2>
                        <p className="text-gray-600">Connect your communication channels for instant customer engagement</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {dataAutomationIntegrations.map((integration, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow text-center">
                                <div className="w-16 h-16 mx-auto mb-4">
                                    <img
                                        src={integration.logo}
                                        alt={integration.name}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                                <p className="text-sm text-gray-600">{integration.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* AI Layer */}
            <section className="py-16 px-4 bg-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-bold mb-4">AI Layer</h2>
                        <p className="text-gray-600">Powered by cutting-edge AI technology</p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {aiLayerIntegrations.map((integration, index) => (
                            <div key={index} className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow text-center">
                                <div className="w-16 h-16 mx-auto mb-4">
                                    <img
                                        src={integration.logo}
                                        alt={integration.name}
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                                <p className="text-sm text-gray-600">{integration.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Our Integrations Work */}
            <section className="py-20 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold mb-4">
                            Why Our Integrations Work
                        </h2>
                        <p className="text-lg text-gray-600">
                            Built For Speed, Security, And Seamless Data Flow
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {whyIntegrationsWork.map((reason, index) => {
                            const IconComponent = reason.Icon;
                            return (
                                <div key={index} className="bg-gray-50 p-8 rounded-2xl text-center">
                                    <div className="w-16 h-16 mx-auto mb-6 bg-white rounded-full flex items-center justify-center">
                                        <IconComponent className="text-orange-500" size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold mb-3">{reason.title}</h3>
                                    <p className="text-gray-600">{reason.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Ready to Connect CTA */}
            <section className="py-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-orange-300 to-purple-400"></div>
                <div className="max-w-4xl mx-auto relative z-10">
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
                        Ready To Connect Your Tools?
                    </h2>
                    <p className="text-lg text-gray-700 mb-8">
                        Set Up Your Integrations In Under 5 Minutes With Our Guided Setup Wizard
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <button onClick={() => {
                            setModalButtonType('demo');
                            setIsSignupModalOpen(true);
                        }} className="bg-orange-600 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-orange-700 transition-all">
                            Start free demo
                        </button>
                        <button className="border-2 border-white bg-white text-gray-800 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all">
                            view setup guide
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 px-4 border-t border-gray-200">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-4 gap-8 mb-8">
                        <div>
                            <h3 className="text-xl font-bold mb-4">
                                <span className="text-gradient">Qorvix</span> — Automate What Matters
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6">
                                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems. From CRM integrations to WhatsApp automations — we build smart, no-code solutions that work.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Contact</h4>
                            <div className="space-y-2 text-sm text-gray-600">
                                <p>London, England, UK</p>
                                <p>+44 7757 233260</p>
                                <p>Support@qorvix.ai</p>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Company</h4>
                            <div className="space-y-2 text-sm">
                                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                                <Link to="/how-it-works" className="block text-gray-600 hover:text-gray-900 transition-colors">How it works</Link>
                            </div>
                        </div>

                        <div>
                            <h4 className="font-bold mb-4">Resources</h4>
                            <div className="space-y-2 text-sm">
                                <a href="" className="block text-gray-600 hover:text-gray-900 transition-colors">Documentation</a>
                                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">Integration</Link>
                                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">FAQs</Link>
                                <a href="" className="block text-gray-600 hover:text-gray-900 transition-colors">Help Center</a>
                                <Link to="/roadmap" className="block text-gray-600 hover:text-gray-900 transition-colors">Roadmap</Link>
                            </div>
                        </div>
                    </div>

                    <div className="pt-8 flex items-center justify-between border-t border-gray-200">
                        <div className="text-2xl font-bold text-gradient">Qorvix</div>
                        <div className="text-sm text-gray-600">
                            © Qorvix Technologies. All Rights Reserved. Built with purpose and precision by <span className="font-semibold">Qorvix Team</span>.
                        </div>
                    </div>
                </div>
            </footer>
            {/* Signup Modal */}
            <SignupFormModal
                isOpen={isSignupModalOpen}
                onClose={() => setIsSignupModalOpen(false)}
                buttonType={modalButtonType}
            />
        </div>
    );
}